import { router, usePage } from "@inertiajs/react"
import { type Consumer, createConsumer } from "@rails/actioncable"
import { Bell, CheckCheck } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Spinner } from "@/components/ui/spinner"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { csrfFetch } from "@/lib/csrf-fetch"
import { cn } from "@/lib/utils"
import type { NotificationItem, SharedProps } from "@/types"

// Module-local consumer singleton — same pattern as use-facility-changed /
// useEdgeStatus: one WebSocket per consumer module, lazy-init, never torn
// down (individual subscriptions unsubscribe on unmount).
let sharedConsumer: Consumer | null = null

function getSharedConsumer(): Consumer {
  if (!sharedConsumer) {
    sharedConsumer = createConsumer()
  }
  return sharedConsumer
}

interface BellPush {
  notification: NotificationItem
  unread_count: number
}

function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? "yesterday" : `${days}d ago`
}

// Header bell: unread badge from the shared unread_notifications prop
// (refreshed on every Inertia navigation), list fetched lazily on open.
// One inbox row per alert transition; clicking through opens the zone.
export default function NotificationBell() {
  const { unread_notifications, organization } = usePage<SharedProps>().props
  const routes = useOrgRoutes()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[] | null>(null)
  const [unread, setUnread] = useState<number | null>(null)

  // Server count wins until the user interacts in this page-visit.
  const unreadCount = unread ?? unread_notifications ?? 0

  // Live updates: NotificationsChannel pushes each new inbox row + the fresh
  // unread count as the alert job writes it. Streams are org-scoped, so skip
  // subscribing until org context exists.
  const orgId = organization?.id ?? null
  useEffect(() => {
    if (orgId == null) return

    const subscription = getSharedConsumer().subscriptions.create(
      { channel: "NotificationsChannel", org_id: orgId },
      {
        received: (data: BellPush) => {
          setUnread(data.unread_count)
          setItems((prev) =>
            prev === null ? null : [data.notification, ...prev],
          )
        },
      },
    )
    return () => {
      subscription.unsubscribe()
    }
  }, [orgId])

  const load = async () => {
    try {
      const res = await fetch(routes.notificationsPath(), {
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const body = await res.json()
      setItems(body.notifications)
      setUnread(body.unread_count)
    } catch {
      // Leave the popover on its spinner-less empty state; the badge from
      // shared props is still accurate as of the last navigation.
      setItems([])
    }
  }

  const markRead = async (ids?: number[]) => {
    try {
      const res = await csrfFetch(routes.markNotificationsReadPath(), {
        method: "POST",
        body: JSON.stringify(ids ? { ids } : {}),
      })
      if (!res.ok) return
      const body = await res.json()
      setUnread(body.unread_count)
      setItems(
        (prev) =>
          prev?.map((n) =>
            !ids || ids.includes(n.id) ? { ...n, read: true } : n,
          ) ?? null,
      )
    } catch {
      // Non-fatal — the row stays unread and can be retried.
    }
  }

  const openItem = (item: NotificationItem) => {
    if (!item.read) markRead([item.id])
    setOpen(false)
    if (item.path) router.visit(item.path)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (v && items === null) load()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : "Notifications"
          }
        >
          <Bell className="size-5" />
          {/* text-white, not text-destructive-foreground: that token is stale
              in this theme (it resolves to the SAME red as --destructive,
              rendering the count invisible). The vendored badge/button
              destructive variants pair bg-destructive with text-white. */}
          {unreadCount > 0 && (
            <span className="bg-destructive absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <span className="text-sm font-medium">Notifications</span>
          {unreadCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground h-7 text-xs"
              onClick={() => markRead()}
            >
              <CheckCheck className="mr-1 size-3.5" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items === null ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-5" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No notifications yet.
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openItem(item)}
                className={cn(
                  "hover:bg-accent flex w-full items-start gap-3 border-b px-4 py-3 text-left last:border-b-0",
                  !item.read && "bg-accent/40",
                )}
              >
                <span
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full",
                    item.kind === "resolved"
                      ? "bg-emerald-500"
                      : item.read
                        ? "bg-muted-foreground/30"
                        : "bg-destructive",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="text-muted-foreground block truncate text-xs">
                      {item.body}
                    </span>
                  )}
                  <span className="text-muted-foreground/70 block text-xs">
                    {timeAgo(item.created_at)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
